import { OrderStatus } from '@src/orders/domain/Order';
import { OrderActions } from '@src/orders/domain/OrderList';
import type { ReviewableOrder } from '@src/orders/domain/ReviewableOrder';
import { useGetOrderTransactions } from '@src/orders/hooks/useGetOrderTransactions';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import { OrdersClient } from '@src/orders/services/OrdersClient';
import { useMutation } from '@tanstack/react-query';

const ALLOWED_PROCESSED_STATUSES = [
  OrderStatus.TO_INVOICE,
  OrderStatus.TO_REVIEW,
];

export const useUpdateBillTo = (order: {
  id: string;
  status: OrderStatus;
  allowedActions: Set<OrderActions>;
}) => {
  const { id, status, allowedActions } = order;
  const { refreshOrder } = useRefreshOrder({ id });

  const { mutate: updateBillingInformationProcessed } = useMutation<
    ReviewableOrder,
    unknown,
    { billTo: string }
  >({
    mutationFn: ({ billTo }) =>
      OrdersClient.updateBillingInformation({ orderId: id, billTo }),
    onSuccess: (updatedOrder) => {
      refreshOrder(updatedOrder);
    },
  });

  const { mutate: updateBillingInformationNotProcessed } = useMutation<
    void,
    unknown,
    { billTo: string }
  >({
    mutationFn: ({ billTo }) =>
      OrdersClient.updateOrderBillTo({ orderId: id, billTo }),
    onSuccess: () => {
      refreshOrder();
    },
  });

  const { transactions, isLoadingTransactions } = useGetOrderTransactions({
    orderId: id,
  });

  const hasOnePaidTransaction = transactions.some((transaction) =>
    transaction.isPaid(),
  );

  const canUpdateBillTo =
    allowedActions.has(OrderActions.UPDATE_BILL_TO) &&
    !hasOnePaidTransaction &&
    !isLoadingTransactions;

  const isAllowedProcessedStatus = ALLOWED_PROCESSED_STATUSES.includes(status);
  const updateBillingInformation = isAllowedProcessedStatus
    ? updateBillingInformationProcessed
    : updateBillingInformationNotProcessed;

  return { updateBillingInformation, canUpdateBillTo };
};
